import { createStore, type StoreApi } from "zustand/vanilla";
import {
  type Card,
  type CanvasDoc,
  type Camera,
  type Edge,
  createId,
  emptyDoc,
  nextGroupName,
  normalizeDoc,
} from "./types.ts";

/**
 * The model store. The render and edit layers read from here and mutate
 * through these actions. Camera lives here too so persistence can restore it,
 * but high-frequency camera updates during a pan/zoom gesture are written
 * straight to the Pixi world container and only committed back here on idle
 * (see CanvasView) to avoid per-frame store churn.
 */

export interface DocState {
  doc: CanvasDoc;

  addCard: (partial: Partial<Card> & Pick<Card, "x" | "y">) => Card;
  /** Add a `file` node embedding a vault markdown document at a world point. */
  addFileCard: (file: string, x: number, y: number, markdown?: string) => Card;
  /** Append several already-built cards in one update (e.g. paste). */
  addCards: (cards: Card[]) => void;
  updateCard: (id: string, patch: Partial<Omit<Card, "id">>) => void;
  /**
   * Varios `updateCard` en una sola actualización, sin checkpoint. Lo usa la
   * carga de las notas embebidas: con uno por nota, abrir un lienzo de N notas
   * rearmaba el array de tarjetas N veces.
   */
  updateCards: (patches: ReadonlyMap<string, Partial<Omit<Card, "id">>>) => void;
  /** Translate a set of cards by the same world delta in one update (group move). */
  moveCards: (ids: Iterable<string>, dx: number, dy: number) => void;
  removeCard: (id: string) => void;
  /** Remove several cards in one update. */
  removeCards: (ids: Iterable<string>) => void;
  /** Remove transient cards without adding another undo checkpoint. */
  discardCards: (ids: Iterable<string>) => void;
  /** Wrap cards into a group. */
  groupCards: (ids: Iterable<string>) => string | null;
  /** Dissolve a group, keeping children in place. */
  ungroup: (id: string) => void;
  /**
   * Mover tarjetas de grupo: las saca del grupo que las tuviera y, si `groupId`
   * no es null, las agrega a ése. Es lo que corre al soltar un arrastre dentro
   * (o fuera) de un grupo, así que NO hace checkpoint: el snapshot ya lo tomó
   * el gesto de arrastre y las dos cosas tienen que deshacerse juntas.
   */
  setCardsGroup: (groupId: string | null, ids: Iterable<string>) => void;
  /**
   * Adopción: las tarjetas recién creadas que hayan NACIDO adentro de un
   * contenedor pasan a ser suyas. Es la contracara de `setCardsGroup` al soltar
   * un arrastre adentro, para el otro camino por el que algo aparece en el
   * grupo. Como aquél, no hace checkpoint: corre pegado al alta que ya tomó el
   * suyo y las dos cosas tienen que deshacerse juntas.
   */
  adoptIntoGroups: (ids: Iterable<string>) => void;
  /**
   * Crear un conector. Devuelve null si la arista no es válida: extremos que no
   * existen, o un nodo consigo mismo. El store es la última línea de defensa —
   * una arista huérfana no se puede dibujar.
   */
  addEdge: (partial: Partial<Edge> & Pick<Edge, "fromNode" | "toNode">) => Edge | null;
  /** Alta de varias aristas ya armadas en una sola actualización (pegado,
   *  generador de carga). Evita el O(n²) de rearmar el array una vez por arista. */
  addEdges: (edges: Edge[]) => void;
  updateEdge: (id: string, patch: Partial<Omit<Edge, "id">>) => void;
  removeEdges: (ids: Iterable<string>) => void;
  /**
   * Cambiar el orden de apilado, como el menú Arrange de Figma. Devuelve false
   * si nada se movió (y entonces tampoco hay paso de deshacer). Ver `restack`.
   */
  arrange: (ids: Iterable<string>, op: ArrangeOp) => boolean;

  /**
   * Agrupar varias mutaciones en UN solo paso de deshacer.
   *
   * Cada acción de alta toma su propio checkpoint, así que sin esto "crear un
   * nodo y conectarlo" serían dos `⌘Z`. Adentro de la transacción los
   * checkpoints internos se suprimen; el único que queda es el de la apertura.
   * Anidar transacciones es seguro: sólo la más externa hace checkpoint.
   */
  transaction: (fn: () => void) => void;

  setCamera: (camera: Camera) => void;
  loadDoc: (doc: CanvasDoc) => void;

  /** Record the current cards as an undo checkpoint. Call BEFORE a mutation that
   *  should be undoable as a unit (e.g. once at the start of a drag gesture). */
  snapshot: () => void;
  /** Revert cards to the previous checkpoint. No-op if history is empty. */
  undo: () => void;
  /** Re-apply a checkpoint reverted by undo(). No-op if nothing to redo. */
  redo: () => void;
}

const DEFAULT_CARD_W = 280;
const DEFAULT_CARD_H = 180;

/** How many undo checkpoints to keep before discarding the oldest. */
const MAX_HISTORY = 100;

/**
 * Un punto del historial. Las tarjetas y las aristas viajan JUNTAS: borrar un
 * nodo se lleva sus conectores, y deshacer eso tiene que devolver las dos cosas
 * o quedan aristas apuntando a un nodo que no existe.
 */
interface Snapshot {
  cards: Card[];
  edges: Edge[];
}

/**
 * Las cuatro operaciones de apilado. El orden de apilado ES el orden de
 * `doc.cards`: lo último del array se dibuja arriba y recibe el click primero,
 * igual que los nodos de JSON Canvas. Por eso todo lo que nace (crear, pegar,
 * duplicar) nace arriba de lo que ya estaba.
 */
export type ArrangeOp = "front" | "forward" | "backward" | "back";

function overlaps(a: Card, b: Card): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

/**
 * Lo que no participa del apilado: los contenedores se dibujan en su propia
 * capa, siempre debajo, y los comentarios son DOM, siempre arriba. Moverlos en
 * el array no cambiaría nada de lo que se ve.
 */
function stacks(c: Card): boolean {
  return c.type !== "group" && c.type !== "comment";
}

/**
 * El array de tarjetas con `picked` reapilado según `op`, o el MISMO array si
 * no cambió nada.
 *
 * Al frente y al fondo mueven el bloque entero conservando el orden relativo
 * de lo elegido. Adelante y atrás, en cambio, no saltan un lugar en el array
 * sino que pasan por encima (o por debajo) de la próxima tarjeta que de verdad
 * se SUPERPONE: con cientos de tarjetas, el vecino en el array casi siempre
 * está en otra punta del lienzo, y un paso que no se ve no le sirve a nadie.
 */
export function restack(cards: Card[], picked: Set<string>, op: ArrangeOp): Card[] {
  let next: Card[];
  if (op === "front" || op === "back") {
    const moving = cards.filter((c) => picked.has(c.id));
    const rest = cards.filter((c) => !picked.has(c.id));
    next = op === "front" ? [...rest, ...moving] : [...moving, ...rest];
  } else {
    next = cards.slice();
    const blocks = (c: Card, other: Card) => !picked.has(other.id) && stacks(other) && overlaps(c, other);
    if (op === "forward") {
      // De arriba hacia abajo: así lo ya movido no se cruza con lo que falta.
      for (let i = next.length - 1; i >= 0; i--) {
        const c = next[i];
        if (!picked.has(c.id)) continue;
        let j = i + 1;
        while (j < next.length && !blocks(c, next[j])) j++;
        if (j === next.length) continue;
        next.splice(i, 1);
        next.splice(j, 0, c); // justo encima de la que la tapaba
      }
    } else {
      for (let i = 0; i < next.length; i++) {
        const c = next[i];
        if (!picked.has(c.id)) continue;
        let j = i - 1;
        while (j >= 0 && !blocks(c, next[j])) j--;
        if (j < 0) continue;
        next.splice(i, 1);
        next.splice(j, 0, c); // justo debajo de la que tapaba
      }
    }
  }
  return next.every((c, i) => c === cards[i]) ? cards : next;
}

/** Las aristas que sobreviven al borrado de los nodos `removed`. */
function keepEdges(edges: Edge[], removed: Set<string>): Edge[] {
  return edges.filter((e) => !removed.has(e.fromNode) && !removed.has(e.toNode));
}

/**
 * Saca de los contenedores a los hijos que dejaron de existir.
 *
 * Un id fantasma no rompe nada visible —`moveCards` simplemente no lo
 * encuentra— pero se guarda en el `.canvas` y se acumula, y desde que una
 * tarjeta puede nacer ya adentro del grupo pasa seguido: alcanza con abrir un
 * nodo de texto adentro y descartarlo vacío.
 */
function pruneChildren(cards: Card[], removed: Set<string>): Card[] {
  return cards.map((c) => {
    if (c.type !== "group" || !c.childrenIds) return c;
    const next = c.childrenIds.filter((id) => !removed.has(id));
    return next.length === c.childrenIds.length ? c : { ...c, childrenIds: next };
  });
}

export function createDocStore(initial?: CanvasDoc): StoreApi<DocState> {
  // Undo/redo history holds whole `cards`/`edges` arrays. They are kept outside
  // the store state so pushing a checkpoint never notifies subscribers/render.
  // Both are immutable (every action rebuilds the array), so storing the
  // reference is enough — past snapshots can't be mutated in place.
  let past: Snapshot[] = [];
  let future: Snapshot[] = [];
  // Mientras es true, las acciones no toman checkpoint propio: lo tomó
  // `transaction()` al abrir. Ver la acción para el porqué.
  let inTransaction = false;

  return createStore<DocState>((set, get) => {
    const checkpoint = (): void => {
      if (inTransaction) return;
      const { cards, edges } = get().doc;
      past.push({ cards, edges });
      if (past.length > MAX_HISTORY) past.shift();
      future = [];
    };

    return {
    doc: normalizeDoc(initial ?? emptyDoc()),

    addCard(partial) {
      checkpoint();
      const card: Card = {
        id: partial.id ?? createId(),
        type: partial.type ?? "text",
        x: partial.x,
        y: partial.y,
        width: partial.width ?? DEFAULT_CARD_W,
        height: partial.height ?? DEFAULT_CARD_H,
        markdown: partial.markdown ?? "# New card\n\nWrite in *markdown*…",
        file: partial.file,
        ink: partial.ink,
        shape: partial.shape,
        text: partial.text,
        comment: partial.comment,
        childrenIds: partial.childrenIds,
        // Todo grupo nace con etiqueta; el resto de las tarjetas no la usan.
        name:
          partial.name ??
          (partial.type === "group" ? nextGroupName(get().doc.cards) : undefined),
        color: partial.color,
        updatedAt: partial.updatedAt ?? Date.now(),
      };
      set((s) => ({ doc: { ...s.doc, cards: [...s.doc.cards, card] } }));
      return card;
    },

    addFileCard(file, x, y, markdown = "") {
      return get().addCard({ type: "file", file, x, y, markdown, width: 320, height: 240 });
    },

    addCards(cards) {
      if (cards.length === 0) return;
      checkpoint();
      set((s) => ({ doc: { ...s.doc, cards: [...s.doc.cards, ...cards] } }));
    },

    updateCard(id, patch) {
      set((s) => ({
        doc: {
          ...s.doc,
          cards: s.doc.cards.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        },
      }));
    },

    updateCards(patches) {
      if (patches.size === 0) return;
      set((s) => ({
        doc: {
          ...s.doc,
          cards: s.doc.cards.map((c) => {
            const patch = patches.get(c.id);
            return patch ? { ...c, ...patch } : c;
          }),
        },
      }));
    },

    moveCards(ids, dx, dy) {
      const set_ = ids instanceof Set ? ids : new Set(ids);
      if (set_.size === 0 || (dx === 0 && dy === 0)) return;
      // Expand set to include children of any group being moved
      const expanded = new Set(set_);
      for (const c of get().doc.cards) {
        if (set_.has(c.id) && c.childrenIds) {
          for (const childId of c.childrenIds) expanded.add(childId);
        }
      }
      set((s) => ({
        doc: {
          ...s.doc,
          cards: s.doc.cards.map((c) =>
            expanded.has(c.id) ? { ...c, x: c.x + dx, y: c.y + dy } : c,
          ),
        },
      }));
    },

    removeCard(id) {
      checkpoint();
      const gone = new Set([id]);
      set((s) => ({
        doc: {
          ...s.doc,
          cards: pruneChildren(
            s.doc.cards.filter((c) => c.id !== id),
            gone,
          ),
          edges: keepEdges(s.doc.edges, gone),
        },
      }));
    },

    removeCards(ids) {
      const set_ = ids instanceof Set ? ids : new Set(ids);
      if (set_.size === 0) return;
      checkpoint();
      set((s) => ({
        doc: {
          ...s.doc,
          cards: pruneChildren(
            s.doc.cards.filter((c) => !set_.has(c.id)),
            set_,
          ),
          edges: keepEdges(s.doc.edges, set_),
        },
      }));
    },

    discardCards(ids) {
      const set_ = ids instanceof Set ? ids : new Set(ids);
      if (set_.size === 0) return;
      set((s) => ({
        doc: {
          ...s.doc,
          cards: pruneChildren(
            s.doc.cards.filter((c) => !set_.has(c.id)),
            set_,
          ),
          edges: keepEdges(s.doc.edges, set_),
        },
      }));
    },

    groupCards(ids) {
      const idSet = new Set(ids);
      if (idSet.size < 2) return null;
      const cards = get().doc.cards;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      const children: string[] = [];
      for (const c of cards) {
        if (idSet.has(c.id)) {
          minX = Math.min(minX, c.x);
          minY = Math.min(minY, c.y);
          maxX = Math.max(maxX, c.x + c.width);
          maxY = Math.max(maxY, c.y + c.height);
          children.push(c.id);
        }
      }
      if (children.length < 2) return null;
      const groupId = createId("group");
      const group: Card = {
        id: groupId,
        type: "group",
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        markdown: "",
        childrenIds: children,
        name: nextGroupName(cards),
        updatedAt: Date.now(),
      };
      checkpoint();
      set((s) => ({ doc: { ...s.doc, cards: [...s.doc.cards, group] } }));
      return groupId;
    },

    setCardsGroup(groupId, ids) {
      const moving = ids instanceof Set ? ids : new Set(ids);
      if (moving.size === 0) return;
      set((s) => {
        let changed = false;
        const cards = s.doc.cards.map((c) => {
          if (c.type !== "group") return c;
          const current = c.childrenIds ?? [];
          // Fuera del grupo viejo; dentro del nuevo (sin duplicar).
          const kept = current.filter((childId) => !moving.has(childId));
          const next =
            c.id === groupId
              ? [...kept, ...[...moving].filter((childId) => childId !== c.id)]
              : kept;
          if (next.length === current.length && next.every((v, i) => v === current[i])) {
            return c;
          }
          changed = true;
          return { ...c, childrenIds: next };
        });
        return changed ? { doc: { ...s.doc, cards } } : s;
      });
    },

    adoptIntoGroups(ids) {
      const incoming = ids instanceof Set ? ids : new Set(ids);
      if (incoming.size === 0) return;
      const cards = get().doc.cards;

      // Lo que ya tiene dueño no se toca. Importa al pegar o duplicar un
      // contenedor: sus hijos nuevos vienen con el grupo copiado ya asignado y
      // reasignarlos por geometría podría robárselos al de abajo.
      const owned = new Set<string>();
      for (const c of cards) {
        if (c.type === "group") for (const childId of c.childrenIds ?? []) owned.add(childId);
      }

      const byGroup = new Map<string, string[]>();
      for (const card of cards) {
        // Un contenedor no se adopta: no anidamos grupos.
        if (!incoming.has(card.id) || card.type === "group" || owned.has(card.id)) continue;
        // Se mira el CENTRO de la tarjeta nueva y no el punto donde se hizo el
        // click: es el único dato que existe en TODOS los caminos de alta
        // —herramienta, trazo, archivo soltado, duplicar, pegar— y así los
        // cinco deciden igual.
        const cx = card.x + card.width / 2;
        const cy = card.y + card.height / 2;
        for (let i = cards.length - 1; i >= 0; i--) {
          const g = cards[i];
          if (g.type !== "group") continue;
          if (cx >= g.x && cx <= g.x + g.width && cy >= g.y && cy <= g.y + g.height) {
            const list = byGroup.get(g.id);
            if (list) list.push(card.id);
            else byGroup.set(g.id, [card.id]);
            break;
          }
        }
      }

      for (const [groupId, members] of byGroup) get().setCardsGroup(groupId, members);
    },

    ungroup(id) {
      const card = get().doc.cards.find((c) => c.id === id);
      if (!card || card.type !== "group") return;
      checkpoint();
      // El contenedor desaparece pero sus hijos quedan: sólo caen las aristas
      // enganchadas al contenedor MISMO, no las de las tarjetas de adentro.
      const gone = new Set([id]);
      set((s) => ({
        doc: {
          ...s.doc,
          cards: s.doc.cards.filter((c) => c.id !== id),
          edges: keepEdges(s.doc.edges, gone),
        },
      }));
    },

    addEdge(partial) {
      const { cards } = get().doc;
      // Un nodo consigo mismo no tiene ruta que dibujar.
      if (partial.fromNode === partial.toNode) return null;
      const ids = new Set(cards.map((c) => c.id));
      if (!ids.has(partial.fromNode) || !ids.has(partial.toNode)) return null;
      checkpoint();
      const edge: Edge = {
        ...partial,
        id: partial.id ?? createId("edge"),
        fromNode: partial.fromNode,
        toNode: partial.toNode,
        updatedAt: partial.updatedAt ?? Date.now(),
      };
      set((s) => ({ doc: { ...s.doc, edges: [...s.doc.edges, edge] } }));
      return edge;
    },

    addEdges(edges) {
      if (edges.length === 0) return;
      const ids = new Set(get().doc.cards.map((c) => c.id));
      const valid = edges.filter(
        (e) => e.fromNode !== e.toNode && ids.has(e.fromNode) && ids.has(e.toNode),
      );
      if (valid.length === 0) return;
      checkpoint();
      set((s) => ({ doc: { ...s.doc, edges: [...s.doc.edges, ...valid] } }));
    },

    updateEdge(id, patch) {
      set((s) => ({
        doc: {
          ...s.doc,
          edges: s.doc.edges.map((e) =>
            // `updatedAt` se refresca solo salvo que el patch traiga el suyo:
            // la capa de render decide si repintar una arista por su firma, y
            // esa firma incluye este campo.
            e.id === id ? { ...e, updatedAt: Date.now(), ...patch } : e,
          ),
        },
      }));
    },

    removeEdges(ids) {
      const set_ = ids instanceof Set ? ids : new Set(ids);
      if (set_.size === 0) return;
      checkpoint();
      set((s) => ({
        doc: { ...s.doc, edges: s.doc.edges.filter((e) => !set_.has(e.id)) },
      }));
    },

    arrange(ids, op) {
      const want = ids instanceof Set ? ids : new Set(ids);
      const { cards } = get().doc;
      // Un contenedor elegido arrastra a sus hijos: en Figma, traer un frame al
      // frente trae lo que tiene adentro, y acá eso es lo único que se ve de él.
      const picked = new Set<string>();
      for (const c of cards) {
        if (!want.has(c.id)) continue;
        if (c.type === "group") for (const childId of c.childrenIds ?? []) picked.add(childId);
        else picked.add(c.id);
      }
      for (const c of cards) if (!stacks(c)) picked.delete(c.id);
      if (picked.size === 0) return false;
      const next = restack(cards, picked, op);
      if (next === cards) return false;
      checkpoint();
      set((s) => ({ doc: { ...s.doc, cards: next } }));
      return true;
    },

    transaction(fn) {
      // Anidada: la de afuera ya tomó el checkpoint y es la dueña del flag.
      if (inTransaction) {
        fn();
        return;
      }
      checkpoint();
      inTransaction = true;
      try {
        fn();
      } finally {
        inTransaction = false;
      }
    },

    setCamera(camera) {
      set((s) => ({ doc: { ...s.doc, camera } }));
    },

    loadDoc(doc) {
      past = [];
      future = [];
      inTransaction = false;
      set({ doc: normalizeDoc(doc) });
    },

    snapshot() {
      checkpoint();
    },

    undo() {
      const prev = past.pop();
      if (!prev) return;
      const { cards, edges } = get().doc;
      future.push({ cards, edges });
      set((s) => ({ doc: { ...s.doc, cards: prev.cards, edges: prev.edges } }));
    },

    redo() {
      const next = future.pop();
      if (!next) return;
      const { cards, edges } = get().doc;
      past.push({ cards, edges });
      set((s) => ({ doc: { ...s.doc, cards: next.cards, edges: next.edges } }));
    },
    };
  });
}
